import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Package, PackageService } from '@vendasta/marketplace-packages';
import { App, AppKey, AppPartnerService, AppType, FieldMaskInterface } from '@galaxy/marketplace-apps';
import { map, switchMap, publishReplay, refCount, tap, catchError, skipWhile, filter } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, zip } from 'rxjs';
import { LineItem, ProductActivation, SalesOrdersService } from '@vendasta/sales-orders';
import { convertActivationsToPackageItemActivationStatuses } from './product-activation';
import { PackageItemActivationStatus } from './package-dropdown/sales-package';
import { SalesOrderStoreService } from '../sales-order-store.service';

export interface PackageApiService {
  getMulti(packageIds: string[]): Observable<Package[]>;
}

export interface AppSdkService {
  getMulti(
    appKeys: AppKey[],
    partnerId: string,
    marketId: string,
    projectionFilter: FieldMaskInterface,
    includeNotEnabled: boolean,
  ): Observable<App[]>;
}

export interface SalesOrders {
  ignoreProductActivationError(
    orderId: string,
    businessId: string,
    productActivationUniqueId: string,
  ): Observable<boolean>;
  previewOrderActivations(orderId: string, businessId: string): Observable<ProductActivation[]>;
}

export interface UIPackages {
  packages: Package[];
  products: App[];
  status: PackageItemActivationStatus[];
}

export interface UIProducts {
  products: App[];
  status: PackageItemActivationStatus[];
}

export interface UIAddons {
  addons: App[];
  status: PackageItemActivationStatus[];
}

@Injectable()
export class SalesOrderContentsService implements OnDestroy {
  partnerId: string;
  marketId: string;
  businessId: string;

  uiPackages$: Observable<UIPackages>;
  uiProducts$: Observable<UIProducts>;
  uiAddons$: Observable<UIAddons>;

  orderId$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  orderId$: Observable<string> = this.orderId$$.asObservable();

  specifiedLineItems$$: BehaviorSubject<LineItem[]> = new BehaviorSubject<LineItem[]>([]);
  specifiedLineItems$: Observable<LineItem[]> = this.specifiedLineItems$$.asObservable();

  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();

  validationErrorMsgs = new Map<string, string>([
    ['1', 'App Not Found'],
    ['2', 'No Order Form SubmissionID'],
    ['3', 'Order Form Not Found'],
    ['4', 'Order Form Already Used'],
    ['5', 'Order Form Not Supported'],
    ['6', 'Invalid Entry URL'],
    ['7', 'Entry URL Not Provided'],
    ['8', 'Invalid Dependency'],
    ['9', 'Already Active'],
    ['10', 'Edition Not Provided'],
    ['11', 'Editions Not Supported'],
    ['12', 'Invalid Edition'],
    ['13', 'Trial Not Supported'],
    ['14', 'Account Group Not Found'],
    ['15', 'No Country Information'],
    ['16', 'Unsupported Country'],
    ['17', 'Can Not Change Edition of Trial'],
    ['18', 'App Not Found'],
  ]);

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(PackageService) private readonly packageSdk: PackageApiService,
    @Inject(AppPartnerService) private readonly appSdk: AppSdkService,
    @Inject(SalesOrdersService) private readonly salesOrdersSdk: SalesOrders,
    private readonly salesOrderStoreService: SalesOrderStoreService,
  ) {
    const orderState$ = this.orderId$.pipe(
      skipWhile((orderId) => orderId === null),
      switchMap((orderId) => this.salesOrderStoreService.getOrderState(orderId, this.businessId)),
      publishReplay(1),
      refCount(),
    );

    this.subscriptions.push(
      orderState$
        .pipe(
          tap((orderState) => {
            if (orderState.loading) {
              this.loading$$.next(true);
            }
          }),
        )
        .subscribe(),
    );

    const order$ = orderState$.pipe(
      filter((orderState) => !orderState.loading),
      map((orderState) => orderState.order),
      publishReplay(1),
      refCount(),
    );

    const combinedLineItems$ = combineLatest([order$, this.specifiedLineItems$]).pipe(
      map(([order, lineItems]) => lineItems.concat(order?.lineItems || [])),
      publishReplay(1),
      refCount(),
    );

    const packages$ = combinedLineItems$.pipe(
      switchMap((lineItems) => {
        const packageLineItems = lineItems.filter((li) => !!li.packageId);
        if (packageLineItems.length > 0) {
          return this.packageSdk.getMulti(packageLineItems.map((li) => li.packageId));
        }
        return of([]);
      }),
      publishReplay(1),
      refCount(),
    );

    const productActivations$ = order$.pipe(
      switchMap((order) => {
        if (order.productActivations) {
          return of(order.productActivations);
        }
        return this.salesOrdersSdk.previewOrderActivations(order.orderId, order.businessId);
      }),
      catchError(() => {
        return of([]);
      }),
      publishReplay(1),
      refCount(),
    );

    const packageItemActivationStatus$ = productActivations$.pipe(
      map((productActivations) => convertActivationsToPackageItemActivationStatuses(productActivations || [])),
      publishReplay(1),
      refCount(),
    );

    const uniqueAppsAndAddons$ = zip(combinedLineItems$, packages$).pipe(
      switchMap(([lineItems, packages]) => {
        const notPackages = lineItems.filter((li) => !!li.appKey);
        const appKeys = notPackages.map(
          ({ appKey }) => new AppKey({ appId: appKey.appId, editionId: appKey.editionId || '' }),
        );

        packages.map((p) =>
          (p.lineItems.lineItems || []).map((li) =>
            appKeys.push(new AppKey({ appId: li.id, editionId: li.editionId || '' })),
          ),
        );
        const uniqueAppKeys = appKeys.reduce((unique, app) => {
          if (unique.find((u) => u.appId === app.appId && u.editionId === app.editionId)) {
            return unique;
          }

          unique.push(app);
          return unique;
        }, []);
        return this.appSdk.getMulti(uniqueAppKeys, this.partnerId, this.marketId, null, true);
      }),
      map((apps) => apps?.filter((a) => !!a?.key?.appId)),
      publishReplay(1),
      refCount(),
    );

    this.uiPackages$ = zip(combinedLineItems$, packages$, uniqueAppsAndAddons$, packageItemActivationStatus$).pipe(
      map(([lineItems, packages, uniqueAppsAndAddons, statuses]) => {
        const packagesLineItems = lineItems.filter((li) => !!li.packageId);
        const quantifiedPackages = quantifyLineItems(packagesLineItems);
        const storePackages = quantifiedPackages
          .map((li) => {
            return packages.find((p) => p.packageId === li.packageId);
          })
          .filter((p) => !!p);
        return {
          packages: storePackages,
          products: uniqueAppsAndAddons,
          status: statuses,
        };
      }),
    );

    this.uiProducts$ = zip(combinedLineItems$, uniqueAppsAndAddons$, packageItemActivationStatus$).pipe(
      map(([lineItems, uniqueAppsAndAddons, statuses]) => {
        const nonPackageLineItems = lineItems.filter((li) => !!li.appKey);
        const quantifiedNonPackage = quantifyLineItems(nonPackageLineItems);
        const apps = uniqueAppsAndAddons?.filter((a) => a.appType === AppType.APP_TYPE_APP);

        const products = quantifiedNonPackage
          .map((npli) => {
            return apps.find(
              (a) => npli.appKey.appId === a.key.appId && (npli.appKey.editionId || '') === (a.key.editionId || ''),
            );
          })
          .filter((a) => !!a);
        return {
          products: products,
          status: statuses,
        };
      }),
    );

    this.uiAddons$ = zip(combinedLineItems$, uniqueAppsAndAddons$, packageItemActivationStatus$).pipe(
      map(([lineItems, uniqueAppsAndAddons, statuses]) => {
        const nonPackageLineItems = lineItems.filter((li) => !!li.appKey);
        const quantifiedNonPackage = quantifyLineItems(nonPackageLineItems);
        const addons = uniqueAppsAndAddons?.filter((a) => a.appType === AppType.APP_TYPE_ADDON);

        const nonPackagedAddons = quantifiedNonPackage
          .map((npli) => {
            return addons.find(
              (a) => npli.appKey.appId === a.key.appId && (npli.appKey.editionId || '') === (a.key.editionId || ''),
            );
          })
          .filter((a) => !!a);
        return {
          addons: nonPackagedAddons,
          status: statuses,
        };
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  initialize(partnerId: string, marketId: string, businessId: string, orderId: string, lineItems: LineItem[]): void {
    this.partnerId = partnerId;
    this.marketId = marketId;
    this.businessId = businessId;
    this.orderId$$.next(orderId || '');
    this.specifiedLineItems$$.next(lineItems || []);
    this.subscriptions.push(
      zip(this.uiPackages$, this.uiProducts$, this.uiAddons$)
        .pipe(tap(() => this.loading$$.next(false)))
        .subscribe(),
    );
  }

  ignoreErrors(orderId: string, businessId: string, itemId: string): Observable<boolean> {
    return this.salesOrdersSdk
      .ignoreProductActivationError(orderId, businessId, itemId)
      .pipe(tap(() => this.salesOrderStoreService.refreshOrder(orderId, businessId)));
  }

  clearOrderStateCache(): void {
    this.salesOrderStoreService.clearOrderStateCache();
  }
}

function flattenArray(array: any[]): LineItem[] {
  return [].concat(...array);
}

function quantifyLineItems(lineItems: LineItem[]): LineItem[] {
  const quantifiedLineItems = lineItems.map((lineItem) => {
    const replicatedLineItems = [];
    const quantity = lineItem.quantity || 1;
    for (let i = 0; i < quantity; i += 1) {
      replicatedLineItems.push(lineItem);
    }

    return replicatedLineItems;
  });

  return flattenArray(quantifiedLineItems);
}
